import React, { useEffect, useState } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ageCalculate } from "../../../utils/utils.js";
import AdminDataTable from "../../../components/common/AdminDataTable.jsx";
import { memberColumns } from "../../../tables/memberColumns.js";
import { can } from "../../../utils/can.js";
import toast from "../../../utils/toast";
import * as XLSX from "xlsx";

const INITIAL_FILTERS = {
  gender: "",
  membership: "",
  verification: "",
  recent_login: "",
  status: "",
  newSignUp: "",
  aadhar_verification: "",
  police_verification: "",
  photo_approval: "",
  caste_approval: "",
  education_approval: "",
  job_income_approval: "",
  is_deleted: ""
};

const getMemberListTitle = (slug, filter, gender) => {
  if (filter === "dailyNewRegistration") {
    return "Today New Registration";
  }

  if (filter === "weeklyNewRegistration") {
    return "Weekly Registration";
  }

  if (filter === "dailyNewPaidMembers") {
    return "Today New Paid Members";
  }

  if (filter === "weeklyNewPaidMembers") {
    return "Weekly Paid Members";
  }

  if (slug === "paid-member") {
    return "Paid Members";
  }

  if (slug === "deleted-member") {
    return "Deleted Members";
  }

  return `All ${gender ? `${gender.charAt(0).toUpperCase() + gender.slice(1)} ` : ""}Members`;
};

const expireCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const setMemberCookie = (name, value) => {
  const expires = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/; SameSite=Lax`;
};

export default function MemberList() {
  // Modal state for profile approval
  const [profileModal, setProfileModal] = useState({ open: false, row: null });
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.includes('paid-member')
    ? 'paid-member'
    : location.pathname.includes('deleted-member')
      ? 'deleted-member'
      : 'all-member';
  const [pageTitle, setPageTitle] = useState('All Members');
  const PAGE_SIZE = 10;
  const EXPORT_PAGE_SIZE = 2000;
  const [success, setSuccess] = useState("");
  const [pageSize, setPageSize] = useState(PAGE_SIZE); // default 10


  const [listLoading, setListLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState("");

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  //const [active, setActive] = useState(true);
  const { register, handleSubmit, reset, watch } = useForm();
  const [userList, setUserList] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [searchVal, setSearchVal] = useState({})
  const [showFilter, setShowFilter] = useState(false);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const watchedValues = watch();

  const hasFormValues = Boolean(
    (watchedValues?.profile_id && String(watchedValues.profile_id).trim() !== "") ||
    (watchedValues?.profile_completion && String(watchedValues.profile_completion).trim() !== "") ||
    (watchedValues?.name && String(watchedValues.name).trim() !== "") ||
    (watchedValues?.mobile && String(watchedValues.mobile).trim() !== "") ||
    (watchedValues?.fromDate && String(watchedValues.fromDate).trim() !== "") ||
    (watchedValues?.toDate && String(watchedValues.toDate).trim() !== "")
  );

  const hasFilterValues = Object.entries(filters || {}).some(
    ([key, val]) => val !== undefined && val !== null && String(val).trim() !== ""
  );

  const hasSearchValues = Object.entries(searchVal || {}).some(
    ([key, val]) => val !== undefined && val !== null && String(val).trim() !== ""
  );

  const urlParams = new URLSearchParams(location.search);
  const hasUrlFilters = Array.from(urlParams.keys()).some(
    (key) => Boolean(urlParams.get(key)?.trim())
  );

  const canClear = hasFormValues || hasFilterValues || hasSearchValues || hasUrlFilters;

  const filteredUsers = users.filter((user) => {

    // Membership Filter
    if (filters.membership && user.membership !== filters.membership) {
      return false;
    }

    // Verification Filter
    if (filters.verification) {
      if (!user[filters.verification]) {
        return false;
      }
    }

    // Recent Login Filter
    if (filters.recent_login === "last_7_days") {
      const lastLogin = new Date(user.last_login);
      const today = new Date();
      const diffDays = (today - lastLogin) / (1000 * 60 * 60 * 24);

      if (diffDays > 7) {   // last 7 days login
        return false;
      }
    }

    return true;
  });

  const API_URL = process.env.REACT_APP_BASE_URL_API;

  const getMemberPortalBase = () => {
    if (process.env.REACT_APP_MEMBER_URL) return process.env.REACT_APP_MEMBER_URL;

    if (typeof window !== "undefined") {
      const { hostname, protocol } = window.location;
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "http://localhost:3001";
      }
      if (hostname.includes("demo2.mightywebber.com") || hostname.includes("demo2.")) {
        return "https://demo3.mightywebber.com";
      }
      return `${protocol}//${hostname}`;
    }

    if (process.env.REACT_APP_HOST_URL) return process.env.REACT_APP_HOST_URL;
    return "http://localhost:3001";
  };

  const MEMBER_PORTAL_BASE = getMemberPortalBase()
    .replace(/\/$/, "")
    .replace(/\/admin$/, "");
  const token = localStorage.getItem("adminToken");

  const buildMemberListUrl = (currentPage = 1, currentLimit = null, search = null, isExplicitClear = false) => {
    const usedLimit = currentLimit || pageSize || PAGE_SIZE;
    const baseUrl = `${API_URL.replace(/\/$/, "")}/api/admin`;
    const endpoint = slug === "paid-member" ? "/user-paid-list" : "/user-list";
    let url = `${baseUrl}${endpoint}?page=${currentPage}&limit=${usedLimit}`;

    const activeSearch = isExplicitClear ? {} : {
      ...(searchVal || {}),
      ...(filters || {}),
      ...(search || {})
    };

    const queryParams = new URLSearchParams(isExplicitClear ? "" : location.search);
    const params = new URLSearchParams();

    Object.keys(activeSearch).forEach((key) => {
      const value = activeSearch[key];
      if (value === null || value === undefined || value === "") {
        return;
      }

      if (key === "newSignUp" && value === "today") {
        params.append("newSignUp", "today");
      } else if (key !== "newSignUp") {
        params.append(key, value);
      }
    });

    if (!isExplicitClear) {
      const addRouteParamIfMissing = (key) => {
        const value = queryParams.get(key) || "";
        if (value && !params.has(key)) {
          params.append(key, value);
        }
      };

      ["filter", "status", "newSignUp", "gender", "fromDate", "toDate", "is_deleted"].forEach(addRouteParamIfMissing);
    }

    if (slug === "deleted-member") {
      params.set("is_deleted", "deleted");
    }

    const queryString = params.toString();
    if (queryString) {
      url += `&${queryString}`;
    }

    return url;
  };

  const fetchUserList = async (search = null, pageNum = 1, limit = null, isExplicitClear = false) => {
    setListLoading(true);

    try {
      const usedLimit = limit || pageSize || PAGE_SIZE;
      const url = buildMemberListUrl(pageNum, usedLimit, search, isExplicitClear);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      });

      const result = await response.json();
      if (result.status) {
        setUserList(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalMembers(result.totalItems || 0);
      }

    } catch (error) {
      console.error("Error fetching member enquiries:", error);
    } finally {
      setListLoading(false);
    }
  };



  useEffect(() => {
    if (slug === 'paid-member') {
      if (can("paid_members", "view") === false) {
        window.location.href = "/admin/dashboard";
        toast.showError("You don't have permission to view paid members");
        return;
      }
    } else if (slug === 'all-member' || slug === 'deleted-member') {
      if (can("all_members", "view") === false && can("deleted_members", "view") === false) {
        window.location.href = "/admin/dashboard";
        toast.showError("You don't have permission to view members");
        return;
      }
    }
    fetchUserList(searchVal, page);
  }
    , [page, pageSize, slug, location.search]);

  useEffect(() => {
    // Close filter panel on slug change
    setShowFilter(false);

    // Reset form inputs
    reset({
      name: "",
      mobile: "",
      profile_id: "",
      profile_completion: "",
      fromDate: "",
      toDate: ""
    });

    const queryParams = new URLSearchParams(location.search);
    const initialGender = queryParams.get("gender") || "";

    // Clear filters on slug change
    setFilters({
      ...INITIAL_FILTERS,
      gender: initialGender
    });

    // Clear search values
    setSearchVal({});
    setPage(1);

    if (slug === 'all-member') {
      const filter = queryParams.get("filter") || "";
      setPageTitle(getMemberListTitle(slug, filter, initialGender));
    } else if (slug === 'paid-member') {
      const filter = queryParams.get("filter") || "";
      setPageTitle(getMemberListTitle(slug, filter, ""));
    } else if (slug === 'deleted-member') {
      setPageTitle("Deleted Members");
    }
  }, [slug, location.search, reset]);

  // search submit handler
  const onSubmit = (search) => {
    const updatedSearch = {
      ...search,
      ...filters
    };

    setSearchVal(updatedSearch);
    setPage(1);
    fetchUserList(updatedSearch, 1);
  };

  const statusChange = async (e, id, status) => {
    e.preventDefault();
    if (!window.confirm("Are you sure you want to change status?")) return;
    const url = `${API_URL.replace(/\/$/, "")}/api/admin/user-list/update/${id}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status: status === 'Active' ? 'Deactive' : 'Active' })
    });

    const result = await response.json();
    if (result.status) {

      fetchUserList(searchVal, page);
    }

  }

  const handleSoftDelete = async (id) => {
    if (can("all_members", "delete") === false) {
      toast.showError("You don't have permission to delete members");
      return;
    }
    if (!window.confirm("Are you sure you want to soft delete this member? Their profile will be hidden from searches and matches, and can be restored later.")) return;

    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/admin/user-list/soft-delete/${id}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.status || result.success) {
        toast.showSuccess(result.message || "User soft deleted successfully");
        fetchUserList(searchVal, page);
      } else {
        toast.showError(result.message || "Failed to soft delete user");
      }
    } catch (err) {
      console.error("Soft delete user error:", err);
      toast.showError("Network error soft deleting user");
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm("Are you sure you want to restore this member profile?")) return;

    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/admin/user-list/restore/${id}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.status || result.success) {
        toast.showSuccess(result.message || "User restored successfully");
        fetchUserList(searchVal, page);
      } else {
        toast.showError(result.message || "Failed to restore user");
      }
    } catch (err) {
      console.error("Restore user error:", err);
      toast.showError("Network error restoring user");
    }
  };

  const handlePermanentDelete = async (id) => {
    if (can("all_members", "delete") === false) {
      toast.showError("You don't have permission to delete members");
      return;
    }
    if (!window.confirm("WARNING: Are you sure you want to PERMANENTLY delete this member? All profile data and uploaded photos will be permanently deleted from servers. This action CANNOT be undone!")) return;

    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/admin/user-list/permanent-delete/${id}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.status || result.success) {
        toast.showSuccess(result.message || "User permanently deleted successfully");
        fetchUserList(searchVal, page);
      } else {
        toast.showError(result.message || "Failed to permanently delete user");
      }
    } catch (err) {
      console.error("Permanent delete user error:", err);
      toast.showError("Network error deleting user");
    }
  };

  const handleDelete = async (id) => {
    return handlePermanentDelete(id);
  };

  const toggleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const handleMemberLogin = async (row) => {
    if (!row?._id) {
      toast.showError("Member not found");
      return;
    }

    if (!window.confirm(`Login as member ${row.profile_id || row.name || "selected user"}?`)) {
      return;
    }

    // Open tab immediately from user action to reduce popup blocking.
    const targetWindow = window.open("", "_blank");
    if (!targetWindow) {
      toast.showError("Popup blocked. Please allow popups and try again.");
      return;
    }

    // Keep new tab isolated from the admin panel window.
    try {
      targetWindow.opener = null;
    } catch (e) {
      // Ignore if browser blocks opener write.
    }

    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/admin/member-login-as-user/${row._id}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok || !result.status) {
        targetWindow.close();
        toast.showError(result.message || "Unable to login as member");
        return;
      }

      expireCookie("authTokenUser");
      expireCookie("authTokenUserLogin");
      expireCookie("otp-data");
      setMemberCookie("authTokenUserLogin", JSON.stringify(result.token));

      const redirectPath = result?.data?.redirect_url || "/dashboard";
      const basePortalUrl = MEMBER_PORTAL_BASE;
      const targetUrl = new URL(redirectPath, `${basePortalUrl}/`);
      targetUrl.searchParams.set("adminToken", result.token);

      targetWindow.location.replace(targetUrl.toString());
      targetWindow.focus();

      toast.showSuccess(result.message || "Member logged in successfully");
    } catch (error) {
      targetWindow.close();
      console.error("Member login error:", error);
      toast.showError("Unable to login as member");
    }
  };


  const handleExportExcel = async () => {
    try {
      setListLoading(true);
      setIsExporting(true);
      setExportProgress(0);
      setExportMessage("Preparing export, please wait...");
      toast.showSuccess("Preparing export, please wait...");

      const allRows = [];
      let currentPage = 1;
      let hasMore = true;
      let totalItems = 0;

      while (hasMore) {
        const url = buildMemberListUrl(currentPage, EXPORT_PAGE_SIZE, searchVal || {});
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json();

        if (!response.ok || !result.status) {
          throw new Error(result.message || "Failed to fetch export data");
        }

        const pageData = Array.isArray(result.data) ? result.data : [];
        if (!pageData.length) {
          hasMore = false;
          break;
        }

        allRows.push(...pageData);
        totalItems = Number(result.totalItems || 0);
        const nextProgress = totalItems > 0 ? Math.min(100, Math.round((allRows.length / totalItems) * 100)) : 0;
        setExportProgress(nextProgress);
        setExportMessage(`Exporting ${allRows.length} of ${totalItems || allRows.length} records...`);

        if (allRows.length >= totalItems || pageData.length < EXPORT_PAGE_SIZE) {
          hasMore = false;
          break;
        }

        currentPage += 1;
      }

      if (!allRows.length) {
        toast.showError("No data to export");
        return;
      }

      const rows = allRows.map((row) => ({
        "User ID": row.profile_id || "-",
        "Name": row.name || "-",
        "Gender": row.gender || "-",
        "Signup Date": row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-IN") : "-",
        "Signup Source": row.signup_source_type || (row.reference ? `Ref: ${row.reference}` : "Direct / Web"),
        "Membership Plan": row.membershipPlan?.name || "Free",
        "Profile": row.profile_completion || `${row.profile_completion_pct || 0}%`,
        "Status": row.status || "-",
        "Mobile Number": row.contact_no || row.mobile || "-",
        "Email ID": row.contact_email || row.email || "-",
        "City Location": row.loc_city?.name || (typeof row.loc_city === 'string' ? row.loc_city : "-"),
        "Profile Approval": row.profileApproval || "Pending",
        "Photo Approval": row.photoApproval || "Pending",
        "Last Active": row.last_login ? new Date(row.last_login).toLocaleDateString("en-IN") : "-"
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Members");

      const fileName = `${slug === "paid-member" ? "Paid_Members" : "All_Members"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      setExportProgress(100);
      setExportMessage(`Completed export for ${allRows.length} records.`);
      toast.showSuccess(`Excel exported successfully (${allRows.length} records)`);
    } catch (error) {
      console.error("Export error:", error);
      setExportMessage("Export failed.");
      toast.showError("Failed to export Excel");
    } finally {
      setListLoading(false);
      setIsExporting(false);
    }
  };

  // Handler for status click in table
  const onProfileStatusClick = (row) => {
    setProfileModal({ open: true, row });
  };

  // Handler for approve/reject action (profile approval only)
  const [remark, setRemark] = useState("");
  const handleProfileStatusChange = async (newStatus) => {
    if (!profileModal.row) return;
    const _id = profileModal.row._id;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/profile-management/profile-approval-request`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            user_id: _id,
            type: 'profile',
            status: newStatus,
            remark,
          })
        }
      );
      const result = await response.json();
      if (result.status) {
        toast.showSuccess(result.message || `Profile status changed to ${newStatus}`);
        // Optimistically update the userList for immediate UI feedback
        setUserList(prevList => prevList.map(user =>
          user._id === _id ? { ...user, profileApproval: newStatus } : user
        ));
      } else {
        toast.showError(result.message || "Failed to change profile status");
      }
    } catch (error) {
      toast.showError("Error updating profile status");
    } finally {
      setProfileModal({ open: false, row: null });
      setRemark("");
      fetchUserList(searchVal, page);
    }
  };

  const columns = memberColumns({
    statusChange,
    handleDelete,
    handleSoftDelete,
    handleRestore,
    handlePermanentDelete,
    handleMemberLogin,
    PAGE_SIZE,
    onProfileStatusClick
  });
  const handleSort = (column) => {
    if (!column) return;

    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    const updatedFilters = {
      ...filters,
      [name]: value
    };

    setFilters(updatedFilters);

    const updatedSearch = {
      ...searchVal,
      ...updatedFilters
    };

    setSearchVal(updatedSearch);
    setPage(1);

    fetchUserList(updatedSearch, 1);
  };

  const clearFilters = () => {
    // Reset all filters and search values
    const emptyFilters = INITIAL_FILTERS;

    // Reset react-hook-form values
    reset({
      name: "",
      mobile: "",
      profile_id: "",
      profile_completion: "",
      fromDate: "",
      toDate: ""
    });

    setFilters(emptyFilters);
    setSearchVal({});
    setPage(1);

    if (location.search || window.location.search) {
      navigate(location.pathname, { replace: true });
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Instantly fetch clean list on 1st click
    fetchUserList({}, 1, null, true);
  };



  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle={pageTitle} />

        <div className="right_col" role="main">
          <div className="x_panel">
            <div className="x_title">
              <h2>{pageTitle}</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <Link to="#" className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </Link>
                </li>

                <li>
                  <Link to="#" className="close-link">
                    <i className="fa fa-close" />
                  </Link>
                </li>
              </ul>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              <div className="row">
                <div className="col-sm-12">
                  {/* filter start */}
                  <div className="filtercont" style={{ marginBottom: "10px", padding: "10px 14px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      {/* Search inputs row */}
                      <div className="row g-2">
                        <div className="col-12 col-sm-4 col-md-2">
                          <label style={{ fontSize: "11px", fontWeight: "600", color: "#475569", marginBottom: "2px", display: "block" }}>Profile ID</label>
                          <input type="text" {...register("profile_id")} className="form-control form-control-sm" style={{ height: "30px", fontSize: "11px" }} placeholder="e.g., VP123456" />
                        </div>
                        <div className="col-12 col-sm-4 col-md-2">
                          <label style={{ fontSize: "11px", fontWeight: "600", color: "#475569", marginBottom: "2px", display: "block" }}>Profile %</label>
                          <select name="profile_completion" {...register("profile_completion")} className="form-control form-control-sm" style={{ height: "30px", fontSize: "11px" }}>
                            <option value="">All Profile %</option>
                            <option value="0-25">0-25%</option>
                            <option value="26-50">26-50%</option>
                            <option value="51-75">51-75%</option>
                            <option value="76-100">76-100%</option>
                          </select>
                        </div>
                        <div className="col-12 col-sm-4 col-md-2">
                          <label style={{ fontSize: "11px", fontWeight: "600", color: "#475569", marginBottom: "2px", display: "block" }}>Search By Name</label>
                          <input type="text" {...register("name")} className="form-control form-control-sm" style={{ height: "30px", fontSize: "11px" }} placeholder="Enter name" />
                        </div>
                        <div className="col-12 col-sm-4 col-md-2">
                          <label style={{ fontSize: "11px", fontWeight: "600", color: "#475569", marginBottom: "2px", display: "block" }}>Email / Mobile</label>
                          <input type="text" {...register("mobile")} className="form-control form-control-sm" style={{ height: "30px", fontSize: "11px" }} placeholder="Mobile or email" />
                        </div>
                        <div className="col-12 col-sm-4 col-md-2">
                          <label style={{ fontSize: "11px", fontWeight: "600", color: "#475569", marginBottom: "2px", display: "block" }}>From Date</label>
                          <input type="date" {...register("fromDate")} className="form-control form-control-sm" style={{ height: "30px", fontSize: "11px" }} max={new Date().toISOString().split("T")[0]} />
                        </div>
                        <div className="col-12 col-sm-4 col-md-2">
                          <label style={{ fontSize: "11px", fontWeight: "600", color: "#475569", marginBottom: "2px", display: "block" }}>To Date</label>
                          <input type="date" {...register("toDate")} className="form-control form-control-sm" style={{ height: "30px", fontSize: "11px" }} max={new Date().toISOString().split("T")[0]} />
                        </div>
                      </div>

                      {/* Action buttons & summary toolbar */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "12px",
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                          <button
                            type="submit"
                            className="btn-small btn-default submit sitebtn"
                            style={{
                              height: "30px",
                              padding: "4px 14px",
                              fontSize: "12px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              marginRight: "10px",
                            }}
                          >
                            <i className="fa fa-search" style={{ fontSize: "11px" }} /> Search
                          </button>
                          {canClear && (
                            <button
                              type="button"
                              className="btn-small btn-default sitebtn"
                              onClick={clearFilters}
                              style={{
                                height: "30px",
                                padding: "4px 12px",
                                fontSize: "12px",
                                backgroundColor: "#f1f5f9",
                                border: "1px solid #cbd5e1",
                                color: "#475569",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                marginRight: "12px",
                              }}
                            >
                              <i className="fa fa-times" style={{ fontSize: "10px" }} /> Clear
                            </button>
                          )}
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              backgroundColor: "#fdf2f8",
                              color: "#b82e66",
                              border: "1px solid #fbcfe8",
                              borderRadius: "14px",
                              padding: "3px 12px",
                              fontSize: "11.5px",
                              fontWeight: "600",
                              height: "28px",
                            }}
                          >
                            Total Members: <strong style={{ color: "#9d174d", marginLeft: "2px" }}>({totalMembers})</strong>
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                          <button
                            type="button"
                            className="filterbtn"
                            onClick={handleExportExcel}
                            disabled={isExporting}
                            style={{
                              height: "30px",
                              padding: "4px 14px",
                              fontSize: "12px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              marginRight: "10px",
                            }}
                          >
                            <i className="fa fa-file-excel-o" style={{ color: "#16a34a" }} />
                            {isExporting ? "Exporting..." : "Export Excel"}
                          </button>
                          <button
                            type="button"
                            className="filterbtn"
                            onClick={toggleFilter}
                            style={{
                              height: "30px",
                              padding: "4px 14px",
                              fontSize: "12px",
                              backgroundColor: showFilter ? "#b82e66" : "",
                              color: showFilter ? "#fff" : "",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <i className="far fa-filter" /> Filter
                          </button>
                        </div>
                      </div>

                      {/* Export progress */}
                      {isExporting && (
                        <div style={{ marginTop: "8px", maxWidth: "420px", marginLeft: "auto" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}>
                            <span>{exportMessage}</span>
                            <strong>{exportProgress}%</strong>
                          </div>
                          <div className="progress" style={{ height: "6px" }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${exportProgress}%`, backgroundColor: "#e013ba" }}
                              aria-valuenow={exportProgress}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>
                      )}

                      {/* Secondary filter drawer */}
                      {showFilter && (
                        <div
                          className="filter-cont2"
                          style={{
                            position: "static",
                            right: "auto",
                            width: "100%",
                            marginTop: "8px",
                            zIndex: 1,
                          }}
                        >
                          <div
                            className="filter-wrapper"
                            style={{
                              background: "#ffffff",
                              border: "1px solid #e2e8f0",
                              borderRadius: "6px",
                              padding: "10px 12px",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 style={{ margin: 0, fontSize: "12px", fontWeight: "600", color: "#334155" }}>
                                <i className="far fa-sliders-h me-1" /> Filter By Categories
                              </h6>
                              <span style={{ fontSize: "11px", color: "#64748b" }}>Select options to filter</span>
                            </div>

                            <div className="row g-2">
                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="is_deleted"
                                  value={filters.is_deleted || ""}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">Active Members (Default)</option>
                                  <option value="deleted">Soft Deleted Only</option>
                                  <option value="all">All (Active & Deleted)</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="gender"
                                  value={filters.gender}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Gender</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="membership"
                                  value={filters.membership}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value={""}>All Plan Type</option>
                                  <option value={"6913323a5be8a54eb6b4ab7a"}>Basic Plan</option>
                                  <option value={"691332c75be8a54eb6b4ab88"}>Gold Plan</option>
                                  <option value={"691332df5be8a54eb6b4ab8c"}>Premium Plan</option>
                                  <option value={"691adb901359ae4b3e061973"}>VIP Shaadi</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="recent_login"
                                  value={filters.recent_login}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value={""}>Activity All</option>
                                  <option value={"last_7_days"}>Recent Login (7 days)</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="aadhar_verification"
                                  value={filters.aadhar_verification}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Aadhar Status</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Verified">Verified</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="police_verification"
                                  value={filters.police_verification}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Police Status</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Verified">Approved</option>
                                  <option value="Rejected">Rejected</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="photo_approval"
                                  value={filters.photo_approval}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Photo Status</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Verified">Approved</option>
                                  <option value="Rejected">Rejected</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="caste_approval"
                                  value={filters.caste_approval}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Caste Status</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Verified">Approved</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="education_approval"
                                  value={filters.education_approval}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Education Status</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Verified">Approved</option>
                                </select>
                              </div>

                              <div className="col-12 col-sm-6 col-md-3">
                                <select
                                  name="job_income_approval"
                                  value={filters.job_income_approval}
                                  onChange={handleFilterChange}
                                  className="form-control form-control-sm"
                                  style={{ width: "100%", height: "28px", fontSize: "11px", padding: "2px 6px" }}
                                >
                                  <option value="">All Job & Income</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Verified">Approved</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </form>
                  </div>
                  {/* filter end */}
                  <div className="card-box table-responsive nolengthtable">
                    <AdminDataTable
                      columns={columns}
                      data={userList}
                      page={page}
                      pageSize={pageSize}
                      totalPages={totalPages}
                      loading={listLoading}
                      onPageChange={setPage}
                      onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPage(1);
                        fetchUserList(searchVal, 1, size);
                      }}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>
          {/* bod card end */}
        </div>

        {/* page end */}
        <Footer />

      </div>


      {/* Profile Approval Modal */}
      {profileModal.open && profileModal.row && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 320, maxWidth: 400 }}>
            <h4>Are you sure you want to change profile status?</h4>
            {profileModal.row.profileApproval === 'Rejected' && (
              <div style={{ color: '#e013ba', margin: '10px 0', fontWeight: 500 }}>
                Only approval is allowed for rejected profiles.
              </div>
            )}
            <div style={{ margin: '12px 0' }}>
              <label>Remark (optional):</label>
              <input
                type="text"
                className="form-control"
                value={remark}
                onChange={e => setRemark(e.target.value)}
                placeholder="Enter remark (optional)"
                style={{ marginTop: 4, marginBottom: 8 }}
              />
            </div>
            <div style={{ margin: '16px 0', display: 'flex', gap: 12, justifyContent: 'center' }}>
              {profileModal.row.profileApproval === 'Pending' && (
                <>
                  <button className="btn btn-success" onClick={() => handleProfileStatusChange('Approved')}>Approve</button>
                  <button className="btn btn-danger" onClick={() => handleProfileStatusChange('Rejected')}>Reject</button>
                </>
              )}
              {profileModal.row.profileApproval === 'Rejected' && (
                <button className="btn btn-success" onClick={() => handleProfileStatusChange('Approved')}>Approve</button>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <button className="btn btn-default" onClick={() => { setProfileModal({ open: false, row: null }); setRemark(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}